import * as React from "react";

import { cn } from "@/lib/utils";
import { FieldShell } from "@/components/ui/field-shell";

/**
 * Input types that never show a placeholder, so `:placeholder-shown` can't tell
 * whether they hold a value. Their label stays floated at all times.
 */
const ALWAYS_FLOATED_TYPES = new Set(["color", "date", "datetime-local", "file", "month", "range", "time", "week"]);

/**
 * Text input. With `label` it renders the Material outline (esal ERP look):
 * the label rests inside the box and floats into a notch in the border when
 * focused or filled. Without a label it draws the same-shaped plain outline.
 */
function Input({
  className,
  wrapperClassName,
  label,
  adornment,
  type,
  id: idProp,
  placeholder,
  ...props
}: React.ComponentProps<"input"> & {
  /** Renders the Material outline with this label floating into its notch. */
  label?: React.ReactNode;
  /** Trailing content inside the outline (hint icon, unit). Needs `label`. */
  adornment?: React.ReactNode;
  /** Classes for the outlined wrapper; `className` still targets the input. */
  wrapperClassName?: string;
}) {
  const generatedId = React.useId();
  const isOutlined = label != null || adornment != null;
  const id = idProp ?? (isOutlined ? generatedId : undefined);

  const input = (
    <input
      id={id}
      type={type}
      data-slot="input"
      // A placeholder must exist for `:placeholder-shown` to drive the float;
      // CSS keeps it invisible until the label has moved out of the way.
      placeholder={isOutlined ? (placeholder ?? " ") : placeholder}
      className={cn(
        "file:text-foreground selection:bg-primary selection:text-primary-foreground h-9 w-full min-w-0 py-1 text-base outline-none file:inline-flex file:h-7 file:border-0 file:bg-transparent file:text-sm file:font-medium disabled:pointer-events-none disabled:cursor-not-allowed md:text-sm",
        isOutlined
          ? "field-control"
          : [
              "placeholder:text-muted-foreground dark:bg-input/30 border-input bg-card rounded-md border px-3 shadow-xs transition-[color,box-shadow] disabled:opacity-50",
              "focus-visible:border-ring focus-visible:ring-ring focus-visible:ring-1 focus-visible:ring-inset",
              "aria-invalid:border-destructive",
            ],
        className,
      )}
      {...props}
    />
  );

  if (!isOutlined) return input;

  return (
    <FieldShell
      label={label}
      adornment={adornment}
      htmlFor={id}
      invalid={props["aria-invalid"] === true || props["aria-invalid"] === "true"}
      filled={ALWAYS_FLOATED_TYPES.has(type ?? "")}
      className={wrapperClassName}
    >
      {input}
    </FieldShell>
  );
}

export { Input };
